import type { StagedCapture } from '../shared/desktop-api';
import { keptScanCopy, plural } from './kept-scan-copy';
import { useEffect, useState } from 'react';
import { useModalFocus } from './use-modal-focus';
import { LoaderCircle, RotateCcw, Square, TriangleAlert } from 'lucide-react';

/**
 * What the operator watches while their own scanner is running.
 *
 * The simulated scanner has shown each sheet full size since it was built, and a
 * real scanner showed nothing at all: the acquisition was one awaited call that
 * answered when the last page had gone through, so the screen held a disabled
 * button and a spinner for however long the stack took. The operator's account
 * of their first real batch was that the scanning experience was terrible and
 * they stopped a minute in — pages were arriving and the product was not saying
 * so. A demonstration device that presents better than the hardware is the wrong
 * way round for a product whose subject is scanning.
 *
 * Deliberately the same surface as the simulator's, down to the class names: an
 * operator should not be able to tell from the screen which kind of scanner they
 * pointed at, because nothing about their job is different.
 *
 * There is no total. A feeder does not know how much paper is in it, and a
 * denominator invented from the last scan would be a number nobody measured.
 */
export function LiveScanFeed({
  jobId,
  operationId,
  sourceLabel,
  onStop,
  problem,
  onRetry,
  onClose,
}: {
  jobId: string;
  /** The scan this feed belongs to. Progress from any other scan is ignored. */
  operationId?: string | null;
  sourceLabel: string;
  onStop: () => void;
  /** Why the scanner could not start, said in the window the operator is looking at. */
  problem?: ScanProblem | null;
  onRetry?: () => void;
  onClose?: () => void;
}) {
  const desktop = window.capturePointDesktop;
  const dialogRef = useModalFocus();
  const [sheetNumber, setSheetNumber] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  // Try again starts a new scan in this same window, so the last scan's page,
  // count and pressed Stop are cleared when the scan it shows changes - left in
  // place, the retry showed the failed scan's page and could not be stopped.
  const [shownOperation, setShownOperation] = useState(operationId);
  if (shownOperation !== operationId) {
    setShownOperation(operationId);
    setSheetNumber(0);
    setElapsed(0);
    setPreviewPath(null);
    setSheet(null);
    setStopping(false);
  }

  useEffect(
    () =>
      desktop?.onScanProgress((update) => {
        if (update.jobId !== jobId) return;
        // A late event from a scan that has already ended must not inflate this
        // one's count or replace its preview.
        if (operationId && update.operationId !== operationId) return;
        // Never backwards: the events are per sheet and a slow preview read must not
        // let an older one overwrite the count an operator is checking their stack
        // against.
        setSheetNumber((current) => Math.max(current, update.sheet));
        setElapsed(update.elapsedMilliseconds);
        if (update.previewPath) setPreviewPath(update.previewPath);
      }),
    [desktop, jobId, operationId],
  );

  // Only the newest sheet is held. This is a feed of paper going through a
  // machine, not a gallery of what has already gone through it.
  useEffect(() => {
    if (!previewPath || !desktop) return;
    let canceled = false;
    void desktop
      .readPagePreview(previewPath)
      .then((source) => {
        if (!canceled) setSheet(source);
      })
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [previewPath, desktop]);

  const seconds = Math.round(elapsed / 1000);
  const duration =
    seconds < 60
      ? `${seconds}s`
      : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;

  if (problem)
    return (
      <div className="modal-backdrop">
        <section
          ref={dialogRef}
          tabIndex={-1}
          className="sim-scanner-modal scan-problem"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="live-scan-title"
          aria-describedby="live-scan-problem"
        >
          <header className="sim-scanner-head">
            <div>
              <small className="eyebrow">Scanner problem</small>
              <h2 id="live-scan-title">{sourceLabel}</h2>
            </div>
          </header>
          <div className="scan-problem-body" id="live-scan-problem">
            <TriangleAlert size={22} aria-hidden="true" />
            <div>
              <strong>{problem.title}</strong>
              {problem.steps.length > 0 && (
                <ul>
                  {problem.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
              )}
              {problem.detail && <small>{problem.detail}</small>}
            </div>
          </div>
          <footer className="modal-actions">
            <button className="secondary-button" onClick={onClose}>
              Close
            </button>
            {onRetry && (
              <button className="primary-button" onClick={onRetry}>
                <RotateCcw size={14} /> Try again
              </button>
            )}
          </footer>
        </section>
      </div>
    );

  return (
    <div className="modal-backdrop">
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="sim-scanner-modal feeding"
        role="dialog"
        aria-modal="true"
        aria-labelledby="live-scan-title"
      >
        <header className="sim-scanner-head">
          <div>
            <small className="eyebrow">Scanning</small>
            <h2 id="live-scan-title">{sourceLabel}</h2>
          </div>
        </header>
        <div className="sim-scanner-body">
          <div className="sim-scanner-stage">
            <div className={`sim-scanner-sheet ${sheet ? 'has-page' : ''}`}>
              {sheet ? (
                <img src={sheet} alt={`Page ${sheetNumber}`} />
              ) : (
                <div className="sim-scanner-sheet-waiting">
                  <LoaderCircle className="spin" size={20} />
                  <span>Waiting for the first page</span>
                </div>
              )}
            </div>
            <p className="sim-scanner-stage-caption">
              {sourceLabel}
              {sheetNumber > 0 && <> · page {sheetNumber}</>}
            </p>
          </div>
        </div>
        <div className="sim-scanner-run active" aria-live="polite">
          <div className="sim-scanner-readout">
            {/* Pages without a total, because that is what is known. The one
                thing this readout must never do is imply the scanner told it how
                much paper was in the feeder. */}
            <span className="sim-scanner-count">
              <strong>{sheetNumber}</strong> {sheetNumber === 1 ? 'page' : 'pages'}
            </span>
            <span>{duration}</span>
          </div>
        </div>
        <footer className="modal-actions">
          <button
            className="secondary-button"
            onClick={() => {
              setStopping(true);
              onStop();
            }}
            disabled={stopping}
          >
            {stopping ? <LoaderCircle className="spin" size={15} /> : <Square size={14} />} Stop
          </button>
        </footer>
      </section>
    </div>
  );
}

import type { ScanProblem } from './scan-problem';
export { scanProblemFor, type ScanProblem } from './scan-problem';

/**
 * Kept pages of an interrupted scan, waiting for the operator.
 *
 * Nothing is read until they choose: the last kept page may be the middle of a
 * document whose remaining pages are still in the feeder. "Scan more" adds the
 * remaining paper behind them in order; "Review kept pages" releases them (and
 * anything scanned after them) to be read. Discard is separate and asks twice.
 */
export function KeptScanDialog({
  capture,
  waitingBehind,
  onRead,
  onScanMore,
  onDiscard,
  onClose,
}: {
  capture: StagedCapture;
  /** Pages scanned after this capture, queued behind it. */
  waitingBehind: number;
  onRead: () => void;
  onScanMore?: () => void;
  onDiscard: () => void;
  onClose: () => void;
}) {
  const desktop = window.capturePointDesktop;
  const dialogRef = useModalFocus();
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // One dialog can be handed the next held scan when this one is settled. The
  // armed Discard belongs to the scan it was armed on: carried over, a single
  // click threw away a scan nobody had asked to discard.
  const [shownCapture, setShownCapture] = useState(capture.id);
  if (shownCapture !== capture.id) {
    setShownCapture(capture.id);
    setConfirmDiscard(false);
  }
  const copy = keptScanCopy(capture.interruption, capture.fileCount);
  useEffect(() => {
    // Cleared first, so a scan with no kept page of its own never shows the last one's.
    setPreview(null);
    if (!capture.lastKeptPage || !desktop) return;
    let canceled = false;
    void desktop
      .readPagePreview(capture.lastKeptPage)
      .then((source) => {
        if (!canceled) setPreview(source);
      })
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [capture.id, capture.lastKeptPage, desktop]);
  return (
    <div className="modal-backdrop">
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="sim-scanner-modal scan-problem kept-scan"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="kept-scan-title"
        aria-describedby="kept-scan-detail"
      >
        <header className="sim-scanner-head">
          <div>
            <small className="eyebrow">{capture.sourceLabel}</small>
            <h2 id="kept-scan-title">{copy.title}</h2>
          </div>
        </header>
        <div className="scan-problem-body kept-scan-body" id="kept-scan-detail">
          {preview ? (
            <figure className="kept-scan-last">
              <img src={preview} alt="Last kept page" />
              <figcaption>Last kept page</figcaption>
            </figure>
          ) : (
            <TriangleAlert size={22} aria-hidden="true" />
          )}
          <div>
            {copy.detail && <p>{copy.detail}</p>}
            {waitingBehind > 0 && (
              <p>
                {plural(waitingBehind, 'more page', 'more pages')} scanned after them{' '}
                {waitingBehind === 1 ? 'is' : 'are'} waiting behind them.
              </p>
            )}
            <small>Nothing is read until you choose.</small>
          </div>
        </div>
        <footer className="modal-actions">
          {confirmDiscard ? (
            <button className="danger-button" onClick={onDiscard}>
              Discard {plural(capture.fileCount, 'page', 'pages')}
            </button>
          ) : (
            <button className="secondary-button" onClick={() => setConfirmDiscard(true)}>
              Discard…
            </button>
          )}
          <button className="secondary-button" onClick={onClose}>
            Later
          </button>
          {onScanMore && (
            <button className="secondary-button" onClick={onScanMore}>
              Scan more
            </button>
          )}
          <button className="primary-button" onClick={onRead}>
            Review kept pages
          </button>
        </footer>
      </section>
    </div>
  );
}
